import { type Dispatch, type SetStateAction, useState } from "react";

import type { ConnectionStatusResponse } from "@hz/common/api-types/auth";
import type { OurStages } from "@hz/common/constants";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Flex, VStack } from "@chakra-ui/react";

import SelectMerchantId from "@components/inputs/select-merchant-id";
import OAuthConnector from "@components/oauth-connector";
import { Button } from "@components/ui/button";

import { useMerchantAccountsListQuery, useUpdateMerchantIdMutation } from "@state/apiSlice/requests";
import { fetchInit } from "@state/mondaySlice";
import type { AppDispatch } from "@state/store";

type ConnectionStepProps = {
  stage: OurStages | null;
  data: {
    isGoogleConnected: boolean;
    isMondayConnected: boolean;
  };
  currentMerchantId?: string | null;
  isLoading: boolean;
  setStep: Dispatch<SetStateAction<number>>;
  isMondayConnected?: boolean;
};

const GoogleConnectionStep = ({
  stage,
  data,
  setStep,
  isLoading,
  isMondayConnected,
  currentMerchantId,
}: ConnectionStepProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [updateMerchantId, { isLoading: isUpdatingMerchantId }] = useUpdateMerchantIdMutation();

  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(
    currentMerchantId ? currentMerchantId : null,
  );

  const {
    data: list,
    isLoading: isListLoading,
    isSuccess,
  } = useMerchantAccountsListQuery({}, { skip: !data.isGoogleConnected });

  const onOauthConnect = async () => {
    console.log("ran oauth func");

    if (selectedMerchantId && selectedMerchantId !== currentMerchantId) {
      await updateMerchantId({ merchantId: selectedMerchantId }).unwrap();
    }

    await dispatch(fetchInit({ forceRefetch: true }));

    if (stage === "COMPLETE") {
      navigate("/", { replace: true });
      return;
    } else {
      setStep(2);
    }
  };

  const merchantOptions =
    Array.isArray(list?.accounts) && list.accounts.length > 0
      ? list.accounts.map((account) => ({
          id: account.accountId,
          description: account.accountName,
        }))
      : [];

  console.log(merchantOptions);

  const onOauthDisconnect = async () => await dispatch(fetchInit({ forceRefetch: true }));

  return (
    <>
      <VStack
        align="flex-start"
        w="full"
        alignSelf="center"
        gapY={"24px"}
      >
        <OAuthConnector
          type="google"
          data={{ isGoogleConnected: data.isGoogleConnected, isMondayConnected: data.isMondayConnected }}
          isLoading={false}
          oAuthCallback={async () => {
            await dispatch(fetchInit({ forceRefetch: true }));
          }}
          oAuthDisconnectCallback={onOauthDisconnect}
        />

        {data.isGoogleConnected && merchantOptions.length > 0 && (
          <SelectMerchantId
            initialId={currentMerchantId ? { id: currentMerchantId, description: "Selected" } : undefined}
            setValue={setSelectedMerchantId}
            options={merchantOptions}
          />
        )}
      </VStack>
      <Flex
        justifyContent="flex-end"
        width="100%"
        mt="24px"
      >
        <Button
          colorPalette="blue"
          onClick={onOauthConnect}
          loading={isLoading || isListLoading}
          disabled={!isMondayConnected || isListLoading || !data.isGoogleConnected}
        >
          Continue
        </Button>
      </Flex>
    </>
  );
};

export default GoogleConnectionStep;
